<template>
  <div>
    <context-menu-icon>
      <v-list dense>
        <v-list-item
          @click="downloadTransactionSepa(transaction.id)"
          v-if="transaction.status === TransactionStatusEnum.WAITING_FOR_DOWNLOAD"
        >
          <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
          <v-list-item-title>{{ trans('transaction.downloadSepa') }}</v-list-item-title>
        </v-list-item>
        <template v-else>
          <template v-if="transaction.status !== TransactionStatusEnum.DONE">
            <v-list-item
              v-if="!['PENDING', 'SENT'].includes(transaction.guestMailStatus)"
              @click="updateGuestMailStatus(transaction, 'PENDING')"
            >
              <v-list-item-icon><v-icon>mdi-email-send</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans(transaction.refundAmount >= 0 ? 'transaction.sendGuestMail' : 'transaction.sendGuestMailPaybackByGuest') }}</v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-if="['PENDING'].includes(transaction.guestMailStatus)"
            @click="updateGuestMailStatus(transaction, 'NOT_NEEDED')"
          >
            <v-list-item-icon><v-icon>mdi-email-remove</v-icon></v-list-item-icon>
            <v-list-item-title>{{ trans('transaction.setGuestMailStatusNotNeeded') }}</v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          v-if="['PENDING', 'SENT'].includes(transaction.guestMailStatus)"
          @click="downloadTransactionPdf(transaction)"
        >
          <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
          <v-list-item-title>{{ trans('transaction.downloadPdf') }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="transaction.lastGuestEmail" @click="getEmail(transaction.lastGuestEmail)">
          <v-list-item-icon><v-icon>mdi-email-search</v-icon></v-list-item-icon>
          <v-list-item-title>{{ trans('emailSettings.showEmail') }}</v-list-item-title>
        </v-list-item>
        <template v-if="transaction.status === TransactionStatusEnum.DONE">
          <v-list-item
            v-if="!['NOT_NEEDED', 'SENT', 'PENDING'].includes(transaction.guestMailStatus)"
            @click="updateGuestMailStatus(transaction, 'NOT_NEEDED')"
          >
            <v-list-item-icon><v-icon>mdi-email-remove</v-icon></v-list-item-icon>
            <v-list-item-title>{{ trans('transaction.setGuestMailStatusNotNeeded') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="!['SENT'].includes(transaction.guestMailStatus)"
            @click="updateGuestMailStatus(transaction, 'SENT')"
          >
            <v-list-item-icon><v-icon>mdi-email-send</v-icon></v-list-item-icon>
            <v-list-item-title>{{ trans('transaction.setGuestMailStatusSent') }}</v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          v-if="!['PENDING', 'NOT_YET_DOWNLOADED', 'MISSING'].includes(transaction.guestMailStatus)"
          @click="updateGuestMailStatus(transaction, 'PENDING')"
        >
          <v-list-item-icon><v-icon>mdi-email-send</v-icon></v-list-item-icon>
          <v-list-item-title>{{ trans('transaction.setGuestMailStatusMissing') }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          @click="markAsCompleted(transaction)"
          v-if="transaction.status !== TransactionStatusEnum.DONE && !['PENDING'].includes(transaction.guestMailStatus)"
        >
          <v-list-item-icon><v-icon>mdi-checkbox-marked-circle</v-icon></v-list-item-icon>
          <v-list-item-title>{{ trans('transaction.markAsCompleted') }}</v-list-item-title>
        </v-list-item>
        <template v-if="!transaction.lastGuestEmail">
          <v-divider></v-divider>
          <v-list-item
            @click="showTransactionDeleteModalForTransaction(transaction)"
          >
            <v-list-item-icon><v-icon>mdi-delete</v-icon></v-list-item-icon>
            <v-list-item-title>{{ trans('transaction.delete') }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </context-menu-icon>

    <email-modal
      :show-modal.sync="showEmailModal"
      :email="currentEmail"
    ></email-modal>

    <confirm-deletion-modal
      :show-modal.sync="showTransactionDeleteModal"
      :title="trans('transaction.delete')"
      @confirm="deleteTransaction"
      @cancel="transactionToBeDeleted = null"
    >
      {{ trans('transaction.deleteConfirm', {bookingId: transactionToBeDeleted ? transactionToBeDeleted.booking.id : null}) }}
    </confirm-deletion-modal>

    <validation-errors-modal
      :title="trans('general.couldNotDownloadSepa')"
      :show-modal.sync="showSepaValidationErrorsModal"
      :validation-errors.sync="sepaValidationErrors"
    >
    </validation-errors-modal>
  </div>
</template>

<script>
import {TransactionApi} from '@/api/transaction-api';
import {TransactionStatus as TransactionStatusEnum} from '@/lib/transaction-status';
import {LocaleUtils} from '@/utils/locale-utils';
import ContextMenuIcon from '@/components/ContextMenuIcon';
import ConfirmDeletionModal from '@/components/ConfirmDeletionModal';
import {MainUtils} from '@/utils/main-utils';
import EmailModal from '@/components/EmailModal';
import {EmailUtils} from '@/utils/email-utils';
import {SepaValidationErrorsUtils} from '@/utils/sepa-validation-errors-utils';
import ValidationErrorsModal from '@/components/ValidationErrorsModal';

export default {
  name: 'TransactionContextMenu',
  components: {
    ValidationErrorsModal,
    EmailModal,
    ConfirmDeletionModal,
    ContextMenuIcon
  },
  mixins: [
    MainUtils,
    LocaleUtils,
    EmailUtils,
    SepaValidationErrorsUtils
  ],
  props: {
    transaction: {
      required: true
    }
  },
  data () {
    return {
      showTransactionDeleteModal: false,
      /** @type {TransactionDto} */
      transactionToBeDeleted: null,
      TransactionStatusEnum: TransactionStatusEnum,
    }
  },
  methods: {
    /** @type {number} */
    downloadTransactionSepa (transactionId) {
      this.downloadingTransactionSepa = true;
      TransactionApi.getTransactionsSepa(transactionId)
        .then(this.downloadResponseAsFile)
        .then(() => this.notifyTransactionChanged())
        .catch(this.handleSepaValidationErrorResponse)
        .finally(() => {
          this.downloadingTransactionSepa = false;
        })
    },
    /** @type {TransactionDto} */
    downloadTransactionPdf (transaction) {
      TransactionApi.downloadPdf(transaction.id)
        .then(this.downloadResponseAsFile)
        .catch(this.handleErrorResponse)
    },
    /** @type {TransactionDto} */
    updateGuestMailStatus (transaction, guestMailStatus) {
      TransactionApi.updateTransaction(transaction.id, {
        guestMailStatus: guestMailStatus
      })
        .then(response => {
          this.showSavedSuccessfullyNotification();
          this.notifyTransactionChanged();
        })
        .catch(this.handleErrorResponse);
    },
    /** @type {TransactionDto} */
    markAsCompleted (transaction) {
      TransactionApi.updateTransaction(transaction.id, {
        status: 'DONE'
      })
        .then(response => {
          this.showSavedSuccessfullyNotification();
          this.notifyTransactionChanged();
        })
        .catch(this.handleErrorResponse);
    },
    /** @type {TransactionDto} */
    showTransactionDeleteModalForTransaction (transaction) {
      this.transactionToBeDeleted = transaction;
      this.showTransactionDeleteModal = true;
    },
    deleteTransaction () {
      if (this.transactionToBeDeleted) {
        this.showTransactionDeleteModal = false;
        TransactionApi.delete(this.transactionToBeDeleted.id)
          .then(response => {
            this.notifyTransactionChanged();
          })
          .catch(this.handleErrorResponse)
          .finally(() => this.transactionToBeDeleted = null)
      }
    },
    notifyTransactionChanged () {
      this.$emit('transaction-changed');
    }
  }
}
</script>

<style scoped>

</style>
